<template>
  <div>
    <div>
      <div class="text-subtitle-2">Edit Assignment for {{ date | formatDate }}</div>
      <p class="text-caption">
        When the permanent assignment is checked, the assigned teacher/classroom
        will be applied to all recurring dates.
      </p>
      <v-divider class="mb-2" />
    </div>

    <div
      v-for="(studentSchedule, index) in sortedStudentSchedules"
      :key="studentSchedule.id"
    >
      <div class="mt-2 d-flex flex-row">
        <span> {{ index + 1 }}. </span>
        <span class="ml-2 flex-grow-1">
          <div class="text-subtitle-2 d-flex justify-space-between">
            <div>
              <span> {{ studentSchedule.studentFirstName }} </span>
              <span>
                {{ studentSchedule.studentLastName }} ({{
                  formatTime(studentSchedule.startTime)
                }}
                - {{ formatTime(studentSchedule.endTime) }})
              </span>

              <span v-if="!globalCenter || !globalCenter.id">
                @ {{ getCenterName(studentSchedule.centerId) }}
              </span>
            </div>

            <div>
              <v-btn
                class="mx-2"
                icon
                small
                color="primary"
                @click="editSchedule(studentSchedule.id)"
              >
                <v-icon> mdi-pencil </v-icon>
              </v-btn>
            </div>
          </div>
          <div v-if="studentSchedule.partOfRecurring">
            Recurring: starts {{ studentSchedule.recurringStartDate }}, every
            {{ studentSchedule.recurringDays.join(", ") }}
            {{
              studentSchedule.recurringEndDate
                ? ` until ${studentSchedule.recurringEndDate}`
                : " (no end date)"
            }}
          </div>
        </span>
      </div>
      <div class="d-flex flex-column mt-4 mx-5">
        <div class="d-flex justify-space-between">
          <v-autocomplete
            @change="studentSchedule.classroomId = null"
            :items="teachers"
            v-model="studentSchedule.teacherId"
            item-value="id"
            :item-text="(item) => `${item.firstName} ${item.lastName}`"
            label="Assigned Teacher"
            hide-details
            dense
            outlined
            clearable
          />
          <span v-if="hasClassrooms" class="mx-2 my-auto"> or </span>
          <v-autocomplete
            v-if="hasClassrooms"
            @change="studentSchedule.teacherId = null"
            :items="getCenterClassrooms(studentSchedule.centerId)"
            v-model="studentSchedule.classroomId"
            item-value="id"
            item-text="roomNumber"
            label="Assigned Classroom"
            hide-details
            dense
            outlined
            clearable
          />
        </div>
        <v-checkbox
          v-if="studentSchedule.partOfRecurring"
          dense
          v-model="studentSchedule.permanent"
          label="Permanent assignment"
        />
      </div>

      <v-divider class="my-4" />
    </div>

    <div class="my-4 text-center">
      <div class="d-flex justify-space-around">
        <v-btn class="mx-auto" @click="assignTeachersOrClassrooms">
          Assign Teachers
          <span v-if="hasClassrooms">/Classrooms</span>
        </v-btn>

        <v-btn color="blue darken-1" text @click="onClose">
          Close
        </v-btn>
      </div>

    </div>
  </div>
</template>

<script>
import dialogMixin from './dialogMixin.js'
import moment from "moment";
import {
  assignTeachersBatch,
  assignClassroomsBatch,
} from "@/services/schedulingService.js";
import { mapGetters } from "vuex";
import { EventBus } from "@/EventBus.js";

import { openEditStudentScheduleInstance } from "@/components/schedule/eventHandler.js";


export default {
  mixins: [dialogMixin],
  props: {
    studentSchedules: {
      type: Array,
      required: true,
    },
    date: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      localStudentSchedules: JSON.parse(JSON.stringify(this.studentSchedules)), // create a deep copy
    };
  },
  computed: {
    ...mapGetters(["globalCenter", "myCenters", "getCenterName"]),

    sortedStudentSchedules() {
      return [...this.localStudentSchedules].sort((a, b) =>
        a.studentFirstName.localeCompare(b.studentFirstName)
      );
    },
    hasClassrooms() {
      return this.classrooms && this.classrooms.length > 0;
    },
  },
  methods: {
    editSchedule(id) {
      const studentSchedule = this.localStudentSchedules.find(
        (ss) => ss.id == id
      );

      openEditStudentScheduleInstance( {studentSchedule} );
    },

    assignTeachersOrClassrooms() {
      const assignTeacherSchedules = this.localStudentSchedules.filter(
        (ss) => ss.teacherId
      );

      if (assignTeacherSchedules && assignTeacherSchedules.length > 0) {
        this.assignTeachers(assignTeacherSchedules);
      }

      const assignClassroomSchedules = this.localStudentSchedules.filter(
        (ss) => ss.classroomId
      );

      if (assignClassroomSchedules && assignClassroomSchedules.length > 0) {
        this.assignClassrooms(assignClassroomSchedules);
      }
    },
    assignClassrooms(assignClassroomSchedules) {
      // construct all student schedules asigned to a classroom
      const schedules = assignClassroomSchedules.map((ss) => {
        // Construct the schedule object
        let schedule = {
          scheduleId: ss.id,
          classroomId: ss.classroomId,
          permanent: ss.permanent,
        };

        // If it's not a permanent schedule, set start and end date to be the same day
        if (!ss.permanent) {
          schedule.scheduleDate = moment(ss.startTime).format("YYYY-MM-DD");
        }

        return schedule;
      });

      assignClassroomsBatch(schedules)
        .then((resp) => {
          // const event = {
          //   type: "assigned-classroom",
          //   data: resp.data,
          // };
          // this.$emit("on-success", event);
          this.onClose();
          EventBus.$emit('reload-chart', {data: resp.data})
        })
        .catch((error) => {
          throw error;
        });
    },
    assignTeachers(assignTeacherSchedules) {
      // construct all student schedules asigned to a teacher
      const schedules = assignTeacherSchedules.map((ss) => {
        // Construct the schedule object
        let schedule = {
          scheduleId: ss.id,
          teacherId: ss.teacherId,
          permanent: ss.permanent,
        };

        // If it's not a permanent schedule, include the scheduleDate
        if (!ss.permanent) {
          schedule.scheduleDate = moment(ss.startTime).format("YYYY-MM-DD");
        }

        return schedule;
      });

      assignTeachersBatch(schedules)
        .then((resp) => {
          // const event = {
          //   type: "assigned-teacher",
          //   data: resp.data,
          //   teacherId: null,
          // };
          // this.$emit("on-success", event);
          this.onClose();
          EventBus.$emit('reload-chart', {data: resp.data})
        })
        .catch((error) => {
          throw error;
        });
    },
    formatTime(timeStr) {
      return moment(timeStr).format("hh:mm A");
    },
    getCenterClassrooms(centerId) {
      if (centerId) {
        return this.classrooms.filter((c) => c.centerId == centerId);
      } else {
        return this.classrooms;
      }
    },
  },
  watch: {
    studentSchedules: {
      handler(newValue) {
        this.localStudentSchedules = JSON.parse(JSON.stringify(newValue));
      },
      deep: true,
    },
  },
};
</script>
